import React, { useEffect, useRef } from "react";
import { prefersReducedMotion } from "../../lib/motion";

/*
 * Signature Three.js visual for the login brand panel: a slow-drifting
 * particle constellation in brand green + gold.
 *
 * Budget rules:
 *  - three is dynamically imported → zero cost to first paint / main bundle
 *  - DPR capped at 2, ~140 unlit points, no textures, no lines
 *  - rAF pauses when the tab is hidden or the canvas leaves the viewport
 *  - never mounts at all under prefers-reduced-motion (CSS fallback remains)
 */
export default function BrandCanvas({ onReady }) {
    const hostRef = useRef(null);
    const onReadyRef = useRef(onReady);
    onReadyRef.current = onReady;

    useEffect(() => {
        if (prefersReducedMotion()) return undefined;

        const host = hostRef.current;
        if (!host) return undefined;

        let disposed = false;
        let renderer = null;
        let scene = null;
        let raf = 0;
        let running = false;
        let inView = true;
        let cleanupFns = [];

        import("three").then(THREE => {
            if (disposed || !hostRef.current) return;

            const width = () => host.clientWidth || 1;
            const height = () => host.clientHeight || 1;

            renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
            renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
            renderer.setSize(width(), height());
            renderer.domElement.className = "brand-canvas";
            renderer.domElement.setAttribute("aria-hidden", "true");
            host.appendChild(renderer.domElement);

            scene = new THREE.Scene();
            const camera = new THREE.PerspectiveCamera(52, width() / height(), 0.1, 60);
            camera.position.z = 14;

            // Two point clouds: green field + sparse gold accents
            const makeCloud = (count, size, color, spread) => {
                const geo = new THREE.BufferGeometry();
                const pos = new Float32Array(count * 3);
                for (let i = 0; i < count * 3; i += 3) {
                    pos[i] = (Math.random() - 0.5) * spread;
                    pos[i + 1] = (Math.random() - 0.5) * spread;
                    pos[i + 2] = (Math.random() - 0.5) * (spread * 0.5);
                }
                geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
                const mat = new THREE.PointsMaterial({
                    size,
                    color,
                    transparent: true,
                    opacity: 0.75,
                    depthWrite: false,
                    sizeAttenuation: true,
                });
                const points = new THREE.Points(geo, mat);
                scene.add(points);
                cleanupFns.push(() => {
                    geo.dispose();
                    mat.dispose();
                });
                return points;
            };

            const green = makeCloud(110, 0.09, 0x3fd9a3, 22);
            const gold = makeCloud(30, 0.13, 0xd9be78, 20);

            const clock = new THREE.Clock();

            const tick = () => {
                raf = requestAnimationFrame(tick);
                const t = clock.getElapsedTime();
                green.rotation.y = t * 0.03;
                green.rotation.x = Math.sin(t * 0.05) * 0.08;
                gold.rotation.y = -t * 0.045;
                gold.rotation.x = Math.cos(t * 0.04) * 0.06;
                renderer.render(scene, camera);
            };

            const start = () => {
                if (!running && inView && document.visibilityState === "visible") {
                    running = true;
                    clock.start();
                    tick();
                }
            };

            const stop = () => {
                running = false;
                cancelAnimationFrame(raf);
            };

            const onVisibility = () => {
                if (document.visibilityState === "visible") start();
                else stop();
            };
            document.addEventListener("visibilitychange", onVisibility);
            cleanupFns.push(() => document.removeEventListener("visibilitychange", onVisibility));

            if (typeof IntersectionObserver !== "undefined") {
                const io = new IntersectionObserver(entries => {
                    inView = entries[0] ? entries[0].isIntersecting : true;
                    if (inView) start();
                    else stop();
                });
                io.observe(host);
                cleanupFns.push(() => io.disconnect());
            }

            const onResize = () => {
                camera.aspect = width() / height();
                camera.updateProjectionMatrix();
                renderer.setSize(width(), height());
            };
            window.addEventListener("resize", onResize);
            cleanupFns.push(() => window.removeEventListener("resize", onResize));

            start();
            if (onReadyRef.current) onReadyRef.current();
        }).catch(() => {
            /* WebGL or chunk load unavailable — the CSS orbit fallback stays visible */
        });

        return () => {
            disposed = true;
            cancelAnimationFrame(raf);
            cleanupFns.forEach(fn => fn());
            if (renderer) {
                renderer.dispose();
                if (renderer.domElement && renderer.domElement.parentNode) {
                    renderer.domElement.parentNode.removeChild(renderer.domElement);
                }
            }
        };
    }, []);

    return <div ref={hostRef} className="brand-canvas-host" aria-hidden="true" />;
}
